import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { showToast } from '@/components/ui/show-toast';
import { useAuthStore } from '@/store/auth';
import { useGetUserQuery, useUpdateUserMutation } from '@/services/user/queries';
import { ChangePasswordDialog } from './modals/change-password';
import { UpdateEmailDialog } from './modals/update-email';
import { DisableAccountDialog } from './modals/disable-account';
import { DeleteAccountDialog } from './modals/delete-account';
import { Lock, Mail, AlertCircle, Trash2, User, Shield } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';

// Define the form schema with Zod
const formSchema = z.object({
  fullName: z.string().min(1, 'Name is required'),
  linkedInUrl: z.string().url('Please enter a valid LinkedIn URL').optional().or(z.literal('')),
  phone: z.string().optional()
});

// Infer the type from the schema
type ProfileFormValues = z.infer<typeof formSchema>;

export default function MyProfileSettings() {
  const { user } = useAuthStore();
  const posthog = usePostHog();

  const { data: userData } = useGetUserQuery(
    user?.id || '',
    {
      enabled: !!user?.id,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false
    }
  );

  const [openModals, setOpenModals] = useState({
    password: false,
    email: false,
    disable: false,
    delete: false
  });
  
  // Initialize the form with React Hook Form and Zod resolver
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: '',
      linkedInUrl: '',
      phone: ''
    }
  });

  const updateUserMutation = useUpdateUserMutation(
    user?.id || '',
    () => {
      posthog?.capture('form_submitted', { form_name: 'update_profile', success: true });
      showToast('success', 'Your profile has been updated successfully.');
    },
    (error) => {
      posthog?.capture('form_submitted', { form_name: 'update_profile', success: false });
      showToast('error', 'Failed to update profile. Please try again.');
      console.error('Profile update error:', error);
    }
  );

  const handleOpenChange = (modal: keyof typeof openModals, isOpen: boolean) => {
    setOpenModals((prev) => ({ ...prev, [modal]: isOpen }));
  };

  // Update form when user data is fetched
  useEffect(() => {
    if (userData) {
      form.reset({
        fullName: userData.fullName || '',
        linkedInUrl: userData.linkedInUrl || '',
        phone: userData.phone || ''
      });
    }
  }, [userData, form]);

  async function onSubmit(values: ProfileFormValues) {
    updateUserMutation.mutate({
      fullName: values.fullName,
      linkedInUrl: values.linkedInUrl || '',
      phone: values.phone || ''
    });
  }
  return (
    <div className='px-6'>
        <Tabs
          defaultValue='profile'
          className='w-full'
          onValueChange={(value) => posthog?.capture('tab_switched', { tab: value, location: 'user_settings' })}
        >
          <TabsList className='inline-flex h-10 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground w-fit mb-8'>
            <TabsTrigger 
              value='profile' 
              className='inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm'
            >
              <User className='mr-2 h-4 w-4' />
              Profile
            </TabsTrigger>
            <TabsTrigger 
              value='security' 
              className='inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm'
            >
              <Shield className='mr-2 h-4 w-4' />
              Security
            </TabsTrigger>
            <TabsTrigger 
              value='management' 
              className='inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm'
            >
              <AlertCircle className='mr-2 h-4 w-4' />
              Management
            </TabsTrigger>
          </TabsList>

        {/* Profile Information Tab */}
        <TabsContent value='profile' className='mt-0'>
          <div className='max-w-2xl'>
            <div className='mb-6'>
              <h2 className='text-xl font-semibold text-gray-900'>Profile Information</h2>
              <p className='mt-2 text-sm text-gray-600'>
                Update your personal information and contact details.
              </p>
            </div>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
                <FormField
                  control={form.control}
                  name='fullName'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='font-semibold'>Full Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='linkedInUrl'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='font-semibold'>LinkedIn URL</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder='https://linkedin.com/in/yourprofile' />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='phone'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className='font-semibold'>Phone Number</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder='+1234567890' />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className='flex justify-end pt-6'>
                <Button
                  isLoading={updateUserMutation.isPending}
                  type='submit'
                  className='px-6'
                  trackingEvent='update_profile_clicked'
                  trackingProperties={{ location: 'user_settings' }}
                >
                  Update Profile
                </Button>
                </div>
              </form>
            </Form>
          </div>
        </TabsContent>

        {/* Account Security Tab */}
        <TabsContent value='security' className='mt-0'>
          <div className='max-w-2xl'>
            <div className='mb-6'>
              <h2 className='text-xl font-semibold text-gray-900'>Account Security</h2>
              <p className='mt-2 text-sm text-gray-600'>
                Manage your account security settings and authentication preferences.
              </p>
            </div>
            
            <div className='space-y-4'>
              {/* Change Password */}
              <div className='flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow'>
                <div className='flex items-start gap-3'>
                  <div className='rounded-lg bg-green-50 p-2'>
                    <Lock className='h-5 w-5 text-green-600' />
                  </div>
                  <div>
                    <h3 className='font-medium text-gray-900'>Change Password</h3>
                    <p className='text-sm text-gray-600 mt-0.5'>
                      Update your password to keep your account secure
                    </p>
                  </div>
                </div>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => handleOpenChange('password', true)}
                  trackingEvent='modal_opened'
                  trackingProperties={{ modal_name: 'change_password' }}
                >
                  Change
                </Button>
              </div>

              {/* Update Email */}
              <div className='flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow'>
                <div className='flex items-start gap-3'>
                  <div className='rounded-lg bg-blue-50 p-2'>
                    <Mail className='h-5 w-5 text-blue-600' />
                  </div>
                  <div>
                    <h3 className='font-medium text-gray-900'>Update Email Address</h3>
                    <p className='text-sm text-gray-600 mt-0.5'>
                      Change your email address for account notifications
                    </p>
                  </div>
                </div>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => handleOpenChange('email', true)}
                  trackingEvent='modal_opened'
                  trackingProperties={{ modal_name: 'update_email' }}
                >
                  Update
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Account Management Tab */}
        <TabsContent value='management' className='mt-0'>
          <div className='max-w-2xl'>
            <div className='mb-6'>
              <h2 className='text-xl font-semibold text-red-600'>Account Management</h2>
              <p className='mt-2 text-sm text-gray-600'>
                Manage your account status and data. These actions are irreversible.
              </p>
            </div>
            
            <div className='space-y-4'>
              {/* Disable Account */}
              <div className='flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50 p-4 shadow-sm hover:shadow-md transition-shadow'>
                <div className='flex items-start gap-3'>
                  <div className='rounded-lg bg-orange-100 p-2'>
                    <AlertCircle className='h-5 w-5 text-orange-600' />
                  </div>
                  <div>
                    <h3 className='font-medium text-gray-900'>Disable Account Temporarily</h3>
                    <p className='text-sm text-gray-600 mt-0.5'>
                      Disable your account temporarily. You can reactivate by logging in.
                    </p>
                  </div>
                </div>
                <Button
                  variant='outline'
                  size='sm'
                  className='border-orange-300 text-orange-700 hover:bg-orange-100'
                  onClick={() => handleOpenChange('disable', true)}
                  trackingEvent='modal_opened'
                  trackingProperties={{ modal_name: 'disable_account' }}
                >
                  Disable
                </Button>
              </div>

              {/* Delete Account */}
              <div className='flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm hover:shadow-md transition-shadow'>
                <div className='flex items-start gap-3'>
                  <div className='rounded-lg bg-red-100 p-2'>
                    <Trash2 className='h-5 w-5 text-red-600' />
                  </div>
                  <div>
                    <h3 className='font-medium text-gray-900'>Delete Account</h3>
                    <p className='text-sm text-gray-600 mt-0.5'>
                      Permanently delete your account and all data. Cannot be undone.
                    </p>
                  </div>
                </div>
                <Button
                  variant='outline'
                  size='sm'
                  className='border-red-300 text-red-700 hover:bg-red-100'
                  onClick={() => handleOpenChange('delete', true)}
                  trackingEvent='modal_opened'
                  trackingProperties={{ modal_name: 'delete_account' }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <ChangePasswordDialog
        open={openModals.password}
        onOpenChange={(isOpen) => handleOpenChange('password', isOpen)}
      />
      <UpdateEmailDialog
        open={openModals.email}
        onOpenChange={(isOpen) => handleOpenChange('email', isOpen)}
      />
      <DisableAccountDialog
        open={openModals.disable}
        onOpenChange={(isOpen) => handleOpenChange('disable', isOpen)}
      />
      <DeleteAccountDialog
        open={openModals.delete}
        onOpenChange={(isOpen) => handleOpenChange('delete', isOpen)}
      />
    </div>
  );
}
